// Round ORG-UAT-1 (2026) — Organizational Chart restructure.
//
// WHY: the previous chart (react-d3-tree) draws a plain top-down tree, so a
// CEO with ~78 direct reports rendered as ONE endless horizontal row that
// scrolled far off-screen, with a huge empty band between levels.
//
// WHAT: rebuilt on d3-org-chart (bumbeishvili/org-chart — the de-facto
// standard for org charts on the web). Its COMPACT layout stacks leaf
// employees in vertical columns under their manager instead of a horizontal
// wall, which is precisely the fix for wide, flat hierarchies. We keep every
// existing behavior: department color coding + legend, employee search,
// expand/collapse all, the click side panel with subordinate breakdown —
// and add fit-to-screen, smooth pan/zoom, compact↔wide toggle, and
// search-that-centers-the-person.
//
// The old implementation (chartContent.js) is untouched — switching the
// import in orgChart.js back is a one-line rollback.
//
// ─────────────────────────────────────────────────────────────────────────
// Round ORG-2 (2026) — completion & enhancement pass. Everything above is
// PRESERVED. Added: "Start from" focus, legend filter, EmpID/email search
// with debounce, richer side panel, CSV export, visible-count readout.
//
// Round ORG-2a (owner feedback):
//   • Disconnected-employees strip REMOVED from this view per owner request
//     (fragments simply don't render; HR still sees them listed in the
//     Data-Quality panel).
//   • Legend chips are now a real FILTER (owner request: "only show those
//     employees"): clicking a department keeps ONLY its members plus the
//     ancestor managers required to keep the tree connected — those
//     connectors render dimmed so the hierarchy is never misleading.
//     Click again (or Clear) to restore. Same behavior in all three views.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { OrgChart } from "d3-org-chart";
import { Card, CardBody, Offcanvas, OffcanvasHeader, OffcanvasBody, Badge, Spinner, Alert } from "reactstrap";
import api from "../../api";
import MyImg from "../../assets/images/users/user-dummy-img.jpg";
import { exportOrgRowsToCsv, lengthOfService, deptColor } from "./orgCsv";
import { Link } from "react-router-dom";
import "./OrgChartPro.css";

// ─── Department palette: deptColor() in orgCsv.js (shared with chartContent,
// go-live parity 2026-09-24 — one fixed colour per department everywhere).

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const OrgChartPro = ({ canEditInHr = false }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const searchTimer = useRef(null);

  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [searchMiss, setSearchMiss] = useState(false);
  const [compact, setCompact] = useState(true);
  const [showLegend, setShowLegend] = useState(true);
  const [selected, setSelected] = useState(null); // node datum for the side panel
  const [focusRootId, setFocusRootId] = useState(null); // Issue 3 — "start from"
  // Round ORG-2a — legend FILTER (resolved department NAME, so codes that
  // share one description — e.g. two HR codes — filter together).
  const [deptFilter, setDeptFilter] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(false);

  const [deptNames, setDeptNames] = useState({}); // jcDept code → description

  // ── Load the existing API payloads ────────────────────────────────────────
  // Round ORG-UAT-1 fix: GET /OrgChart returns NESTED trees (roots with
  // Subordinates recursively populated), not a flat list — flatten here.
  // Round ORG-2: the backend now marks fragment roots isDisconnected=true;
  // we keep the primary tree for the chart and collect fragments for the
  // HR review strip instead of dropping them silently.
  useEffect(() => {
    let alive = true;
    const flattenTrees = (roots) => {
      const out = [];
      const walk = (node, parentEmpId) => {
        if (!node) return;
        out.push({ ...node, supervisorId: parentEmpId ?? node.supervisorId });
        (node.subordinates || node.Subordinates || []).forEach((c) => walk(c, node.empId));
      };
      (Array.isArray(roots) ? roots : [roots]).forEach((r) => walk(r, null));
      return out;
    };
    Promise.all([api.get("OrgChart"), api.get("Department").catch(() => ({ data: [] }))])
      .then(([oc, dep]) => {
        if (!alive) return;
        const allRoots = Array.isArray(oc.data) ? oc.data : [oc.data];
        // Round ORG-2a — fragment roots (isDisconnected=true) are simply not
        // rendered here per owner request; HR reviews them in the
        // Data-Quality panel instead.
        const primaryRoots = allRoots.filter((r) => r && r.isDisconnected !== true);
        setRows(flattenTrees(primaryRoots.length ? primaryRoots : allRoots));
        const list = dep.data?.value ?? dep.data ?? [];
        const map = {};
        (Array.isArray(list) ? list : []).forEach((d) => {
          const code = String(d.jcDept1 ?? d.JcDept1 ?? "").trim();
          if (code) map[code] = d.description ?? d.Description ?? code;
        });
        setDeptNames(map);
      })
      .catch((e) => { if (alive) setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || "Failed to load org chart."); });
    return () => { alive = false; };
  }, []);

  // ── Shape the data: flat {id, parentId} list with EXACTLY one root ───────
  const { flat, deptMeta, byId, fullCount, memberCount } = useMemo(() => {
    if (!rows) return { flat: null, deptMeta: {}, byId: {}, fullCount: 0, memberCount: 0 };

    const ids = new Set(rows.map((r) => String(r.empId)));
    // Root = no supervisor, self-supervised, or supervisor not in the set.
    let rootId = null;
    const shaped = rows.map((r) => {
      const id = String(r.empId).trim();
      let parentId = r.supervisorId != null ? String(r.supervisorId).trim() : "";
      const isRootish = !parentId || parentId === id || !ids.has(parentId);
      if (isRootish) parentId = null;
      return {
        id,
        parentId,
        name: `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() || id,
        title: r.title || "",
        department: r.department != null ? String(r.department).trim() : "",
        email: r.emailAddress || "",
        phone: r.cell || "",            // company cell (backend privacy fix)
        extension: r.extension || "",   // Round ORG-2 — desk extension
        hireDate: r.hireDate || null,
        directReports: r.directReports ?? 0,
        totalReports: r.totalReports ?? 0,
        photo: r.photoBase64 ? `data:image/jpeg;base64,${r.photoBase64}` : null,
        _isRootCandidate: isRootish,
      };
    });

    // Round GEN-UAT-10 fix — DROP orphans from the CHART, don't re-parent
    // them. (Round ORG-2: they're no longer lost — the backend flags them
    // and they render in the "Disconnected / Needs HR Review" strip below.)
    const roots = shaped.filter((n) => n.parentId === null);
    let keptShaped = shaped;
    if (roots.length > 0) {
      rootId = roots[0].id;
      const byParent = {};
      shaped.forEach((n) => {
        (byParent[n.parentId] = byParent[n.parentId] || []).push(n);
      });
      const keep = new Set();
      const stack = [rootId];
      while (stack.length) {
        const id = stack.pop();
        if (keep.has(id)) continue;
        keep.add(id);
        (byParent[id] || []).forEach((c) => stack.push(c.id));
      }
      keptShaped = shaped.filter((n) => keep.has(n.id));
    }
    let shapedConnected = keptShaped;
    const totalConnected = shapedConnected.length;

    // Round ORG-2 (Issue 3) — "Start from" focus: re-root the VISIBLE chart
    // on the selected person + all descendants. Pure view filter; supervisor
    // data untouched. Path-to-CEO stays available via the breadcrumb-like
    // "Show full company" reset.
    if (focusRootId && shapedConnected.some((n) => n.id === focusRootId)) {
      const byParent = {};
      shapedConnected.forEach((n) => {
        (byParent[n.parentId] = byParent[n.parentId] || []).push(n);
      });
      const keep = new Set();
      const stack = [focusRootId];
      while (stack.length) {
        const id = stack.pop();
        if (keep.has(id)) continue;
        keep.add(id);
        (byParent[id] || []).forEach((c) => stack.push(c.id));
      }
      shapedConnected = shapedConnected
        .filter((n) => keep.has(n.id))
        .map((n) => (n.id === focusRootId ? { ...n, parentId: null } : n));
    }

    // Department → color/name map (stable order = first appearance), built
    // BEFORE the legend filter is applied so every department stays clickable
    // in the legend with its true (focus-scope) headcount.
    const meta = {};
    shapedConnected.forEach((n) => {
      const d = n.department || "—";
      if (!meta[d]) meta[d] = {
        key: d,
        name: deptNames[d] || d,
        color: deptColor(d),   // Go-live parity (2026-09-24): fixed colour per department
        count: 0,
      };
      meta[d].count += 1;
    });
    const nameOf = (n) => (meta[n.department || "—"]?.name) || n.department || "—";

    // Round ORG-2a — legend FILTER: keep ONLY the selected department's
    // members, plus the ancestor managers required to keep the tree
    // connected (d3-org-chart needs every parentId to exist). Connectors are
    // flagged _ghost and render dimmed, so nobody reads them as part of the
    // filtered department and the hierarchy is never misleading.
    let memberCount = shapedConnected.length;
    if (deptFilter) {
      const byIdAll = {};
      shapedConnected.forEach((n) => { byIdAll[n.id] = n; });
      const keep = new Set();
      const members = new Set();
      shapedConnected.forEach((n) => {
        if (nameOf(n) !== deptFilter) return;
        members.add(n.id);
        let cur = n;
        while (cur && !keep.has(cur.id)) {
          keep.add(cur.id);
          cur = cur.parentId ? byIdAll[cur.parentId] : null;
        }
      });
      memberCount = members.size;
      shapedConnected = shapedConnected
        .filter((n) => keep.has(n.id))
        .map((n) => (members.has(n.id) ? n : { ...n, _ghost: true }));
    }

    const map = {};
    shapedConnected.forEach((n) => { map[n.id] = n; });
    return { flat: shapedConnected, deptMeta: meta, byId: map, fullCount: totalConnected, memberCount };
  }, [rows, deptNames, focusRootId, deptFilter]);

  // ── Render / re-render the chart ──────────────────────────────────────────
  useEffect(() => {
    if (!flat || !containerRef.current) return;

    const chart = new OrgChart()
      .container(containerRef.current)
      .data(flat)
      .nodeWidth(() => 236)
      .nodeHeight(() => 96)
      .childrenMargin(() => 44)
      .compactMarginBetween(() => 18)
      .compactMarginPair(() => 40)
      .neighbourMargin(() => 22)
      .siblingsMargin(() => 22)
      .compact(compact)
      .nodeContent((d) => {
        const n = d.data;
        const meta = deptMeta[n.department || "—"] || { name: "—", color: "#9EA7B8" };
        const img = n.photo || MyImg;
        const directs = d.data._directSubordinates || 0;
        const total = d.data._totalSubordinates || 0;
        // Round ORG-2a — connector ancestors kept only to link the filtered
        // department to the root render dimmed (_ghost).
        const dimmed = n._ghost ? " ocp-card--dim" : "";
        return `
          <div class="ocp-card${dimmed}" style="width:236px;height:96px">
            <div class="ocp-topbar" style="background:${meta.color}"></div>
            <div class="ocp-body">
              <img class="ocp-avatar" src="${img}" alt="" loading="lazy" />
              <div class="ocp-text">
                <div class="ocp-name" title="${esc(n.name)}">${esc(n.name)}</div>
                <div class="ocp-title" title="${esc(n.title)}">${esc(n.title) || "&nbsp;"}</div>
                <span class="ocp-dept" style="color:${meta.color};border-color:${meta.color}">${esc(meta.name)}</span>
              </div>
            </div>
            ${total > 0 ? `<div class="ocp-count" title="${directs} direct / ${total} total reports">${directs} / ${total}</div>` : ""}
          </div>`;
      })
      .buttonContent(({ node }) => {
        const expanded = node.children;
        const kids = node.data._directSubordinates || 0;
        return `<div class="ocp-expander">${expanded ? "▴" : "▾"} ${kids}</div>`;
      })
      .onNodeClick((d) => {
        setSelected(d.data);
      })
      .render();

    chartRef.current = chart;
    if (deptFilter) {
      // Filtered view: show every kept node so the department's members are
      // all visible immediately, then fit.
      chart.expandAll();
      chart.render().fit();
    } else {
      // Start with a readable view: root + first level, fitted.
      chart.collapseAll();
      chart.setExpanded(flat.find((n) => n.parentId === null)?.id, true).render().fit();
    }

    return () => {
      chartRef.current = null;
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [flat, deptFilter]);

  // Compact ↔ wide toggle without a full re-mount.
  useEffect(() => {
    if (chartRef.current) chartRef.current.compact(compact).render().fit();
  }, [compact]);

  // ── Toolbar actions ───────────────────────────────────────────────────────
  const expandAll = useCallback(() => { chartRef.current?.expandAll(); chartRef.current?.fit(); }, []);
  const collapseAll = useCallback(() => {
    const chart = chartRef.current;
    if (!chart || !flat) return;
    chart.collapseAll();
    const root = flat.find((n) => n.parentId === null);
    if (root) chart.setExpanded(root.id, true);
    chart.render().fit();
  }, [flat]);
  const fitView = useCallback(() => chartRef.current?.fit(), []);
  const zoomIn = useCallback(() => chartRef.current?.zoomIn(), []);
  const zoomOut = useCallback(() => chartRef.current?.zoomOut(), []);
  const centerRoot = useCallback(() => {
    const root = flat?.find((n) => n.parentId === null);
    if (root) chartRef.current?.setCentered(root.id).render();
  }, [flat]);

  // Round ORG-2 (Issue 12) — search matches name / title / department /
  // EmpID / email; debounced so large orgs don't re-highlight per keystroke.
  const findMatch = useCallback((needle) => {
    if (!flat) return null;
    return flat.find(
      (n) =>
        n.name.toLowerCase().includes(needle) ||
        (n.title || "").toLowerCase().includes(needle) ||
        (deptMeta[n.department || "—"]?.name || "").toLowerCase().includes(needle) ||
        n.id.toLowerCase().includes(needle) ||
        (n.email || "").toLowerCase().includes(needle)
    );
  }, [flat, deptMeta]);

  const applySearch = useCallback((q) => {
    const chart = chartRef.current;
    if (!chart || !flat) return;
    chart.clearHighlighting();
    setSearchMiss(false);
    const needle = q.trim().toLowerCase();
    if (!needle) return;
    const hit = findMatch(needle);
    if (hit) chart.setUpToTheRootHighlighted(hit.id).setCentered(hit.id).render();
    else setSearchMiss(true);
  }, [flat, findMatch]);

  const runSearch = useCallback((q) => {
    setSearch(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => applySearch(q), 250);
  }, [applySearch]);

  // Don't leave a debounce timer running after unmount.
  useEffect(() => () => { if (searchTimer.current) clearTimeout(searchTimer.current); }, []);

  const onSearchKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      if (searchTimer.current) clearTimeout(searchTimer.current);
      applySearch(search);
    }
  }, [applySearch, search]);

  // ── Side-panel helpers ────────────────────────────────────────────────────
  const subordinatesOf = useCallback((id) => {
    if (!flat) return [];
    const out = [];
    const walk = (pid) => {
      flat.forEach((n) => {
        if (n.parentId === pid) { out.push(n); walk(n.id); }
      });
    };
    walk(id);
    return out;
  }, [flat]);

  const panelData = useMemo(() => {
    if (!selected) return null;
    const subs = subordinatesOf(selected.id);
    const breakdown = {};
    subs.forEach((s) => {
      const d = deptMeta[s.department || "—"]?.name || "—";
      breakdown[d] = (breakdown[d] || 0) + 1;
    });
    return { subs, breakdown };
  }, [selected, subordinatesOf, deptMeta]);

  const copyEmail = useCallback(async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 1500);
    } catch (_) { /* clipboard unavailable — ignore */ }
  }, []);

  // Round ORG-2 (Issue 5) — CSV export of the CURRENT view (focus root and
  // department spotlight respected). Only already-authorized fields.
  const exportCsv = useCallback((rowsToExport, filename) => {
    if (!rowsToExport?.length) return;
    const nameById = {};
    (flat || []).forEach((n) => { nameById[n.id] = n.name; });
    exportOrgRowsToCsv(
      rowsToExport.map((n) => ({
        name: n.name,
        empId: n.id,
        title: n.title,
        departmentName: deptMeta[n.department || "—"]?.name || n.department || "",
        supervisorName: n.parentId ? (nameById[n.parentId] || "") : "",
        directReports: n.directReports,
        totalReports: n.totalReports,
        email: n.email,
        extension: n.extension,
      })),
      filename
    );
  }, [flat, deptMeta]);

  const exportVisible = useCallback(() => {
    // Export exactly what the view shows as members: dept filter drops
    // non-members (ghost connectors excluded), focus root already applied.
    const list = (flat || []).filter((n) => !n._ghost);
    exportCsv(list, "org-chart.csv");
  }, [flat, exportCsv]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (error) return <Alert color="danger" className="mt-3">{error}</Alert>;
  if (!flat) {
    return (
      <Card><CardBody className="text-center py-5">
        <Spinner color="primary" />
        <div className="text-muted mt-2">Loading organization…</div>
      </CardBody></Card>
    );
  }

  const focusName = focusRootId ? (byId[focusRootId]?.name || flat.find((n) => n.parentId === null)?.name) : null;

  return (
    <Card className="ocp-wrap">
      <CardBody>
        {/* Toolbar */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <div className="ocp-search">
            <i className="ri-search-line" />
            <input
              type="text"
              placeholder="Search name, EmpID, title, dept, email…"
              value={search}
              onChange={(e) => runSearch(e.target.value)}
              onKeyDown={onSearchKeyDown}
            />
          </div>
          {searchMiss && <span className="text-danger small">No matches{focusRootId || deptFilter ? " in the current view — clear focus/filter to widen the search." : "."}</span>}
          <button className="btn btn-light btn-sm border" onClick={fitView} title="Fit the whole chart on screen">
            <i className="ri-focus-3-line me-1" />Fit
          </button>
          <button className="btn btn-light btn-sm border" onClick={zoomIn}><i className="ri-zoom-in-line" /></button>
          <button className="btn btn-light btn-sm border" onClick={zoomOut}><i className="ri-zoom-out-line" /></button>
          <button className="btn btn-light btn-sm border" onClick={centerRoot} title="Center the chart on the top of the visible tree">
            <i className="ri-home-4-line me-1" />Center root
          </button>
          <button className="btn btn-light btn-sm border" onClick={expandAll}>
            <i className="ri-fullscreen-line me-1" />Expand All
          </button>
          <button className="btn btn-light btn-sm border" onClick={collapseAll}>
            <i className="ri-fullscreen-exit-line me-1" />Collapse All
          </button>
          <button
            className={`btn btn-sm border ${compact ? "btn-primary" : "btn-light"}`}
            onClick={() => setCompact((c) => !c)}
            title="Compact stacks team members in columns — best for large teams. Wide is the classic one-row-per-level tree."
          >
            <i className="ri-layout-column-line me-1" />{compact ? "Compact" : "Wide"} layout
          </button>
          <button className="btn btn-light btn-sm border" onClick={() => setShowLegend((v) => !v)}>
            <i className="ri-palette-line me-1" />{showLegend ? "Hide" : "Show"} Legend
          </button>
          <button className="btn btn-light btn-sm border" onClick={exportVisible} title="Download the current view as a CSV (respects focus and department filter)">
            <i className="ri-download-2-line me-1" />Export CSV
          </button>
          <div className="ms-auto text-muted small">
            {deptFilter
              ? <>{deptFilter}: <strong>{memberCount}</strong> of <strong>{fullCount}</strong> employees</>
              : focusRootId
                ? <>Showing <strong>{flat.length}</strong> of <strong>{fullCount}</strong> employees</>
                : <>Total employees: <strong>{flat.length}</strong></>}
          </div>
        </div>

        {/* Round ORG-2 (Issue 3) — focus banner + reset */}
        {focusRootId && (
          <div className="ocp-focus-banner mb-2">
            <i className="ri-crosshair-2-line me-1" />
            Focused on <strong className="mx-1">{focusName}</strong> and their team.
            <button className="btn btn-link btn-sm p-0 ms-2" onClick={() => setFocusRootId(null)}>
              Show full company
            </button>
          </div>
        )}

        {/* Legend — Round ORG-2a: chips are a real FILTER. Click a department
            to show only its members (plus dimmed connector managers); click
            again or Clear to restore. Chips sharing one description (e.g.
            two HR dept codes) merge into a single chip that filters both. */}
        {showLegend && (
          <div className="ocp-legend mb-2">
            {(() => {
              const seen = new Set();
              return Object.values(deptMeta).filter((m) => {
                if (seen.has(m.name)) return false;
                seen.add(m.name);
                return true;
              }).map((m) => {
                const nameCount = Object.values(deptMeta)
                  .filter((x) => x.name === m.name)
                  .reduce((acc, x) => acc + x.count, 0);
                return (
                  <button
                    key={m.name}
                    type="button"
                    className={`ocp-legend-chip ocp-legend-chip--btn ${deptFilter === m.name ? "active" : ""}`}
                    onClick={() => setDeptFilter((cur) => (cur === m.name ? null : m.name))}
                    title={deptFilter === m.name ? "Click to show all departments again" : "Click to show only this department"}
                  >
                    <span className="ocp-dot" style={{ background: m.color }} />
                    {m.name}
                    <span className="ocp-legend-count">{nameCount}</span>
                  </button>
                );
              });
            })()}
            {deptFilter && (
              <button type="button" className="btn btn-link btn-sm p-0 ms-1" onClick={() => setDeptFilter(null)}>
                Clear
              </button>
            )}
          </div>
        )}

        <div className="ocp-tip text-muted small mb-1">
          Tip: drag to pan, scroll to zoom, click the ▾ badge to expand a team, click a card for details.
        </div>

        {/* The chart */}
        <div ref={containerRef} className="ocp-canvas" />

        {/* Detail side panel */}
        <Offcanvas isOpen={!!selected} direction="end" toggle={() => setSelected(null)}>
          <OffcanvasHeader toggle={() => setSelected(null)}>Employee</OffcanvasHeader>
          <OffcanvasBody>
            {selected && (
              <>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <img
                    src={selected.photo || MyImg}
                    alt=""
                    style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover" }}
                  />
                  <div>
                    <h5 className="mb-0">{selected.name}</h5>
                    <div className="text-muted">{selected.title || "—"}</div>
                    <Badge
                      color="light"
                      className="border"
                      style={{ color: (deptMeta[selected.department || "—"] || {}).color }}
                    >
                      {(deptMeta[selected.department || "—"] || {}).name || "—"}
                    </Badge>
                  </div>
                </div>

                {/* Round ORG-2 (Issue 4) — richer facts */}
                <div className="ocp-facts mb-2">
                  <div><span className="ocp-fact-lbl">EmpID</span><span>{selected.id}</span></div>
                  <div><span className="ocp-fact-lbl">Reports</span><span>{selected.directReports || 0} direct / {selected.totalReports || 0} total</span></div>
                  {selected.hireDate && (
                    <div><span className="ocp-fact-lbl">Hired</span><span>{new Date(selected.hireDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span></div>
                  )}
                  {lengthOfService(selected.hireDate) && (
                    <div><span className="ocp-fact-lbl">Service</span><span>{lengthOfService(selected.hireDate)}</span></div>
                  )}
                </div>

                {selected.email && (
                  <div className="mb-1 d-flex align-items-center">
                    <i className="ri-mail-line me-2" />
                    <a href={`mailto:${selected.email}`}>{selected.email}</a>
                    <button
                      className="btn btn-light btn-sm border ms-2 py-0 px-1"
                      onClick={() => copyEmail(selected.email)}
                      title="Copy email address"
                    >
                      <i className={copiedEmail ? "ri-check-line" : "ri-file-copy-line"} />
                    </button>
                  </div>
                )}
                {selected.extension && (
                  <div className="mb-1"><i className="ri-phone-line me-2" />Ext. <a href={`tel:${selected.extension}`}>{selected.extension}</a></div>
                )}
                {selected.phone && (
                  <div className="mb-2"><i className="ri-smartphone-line me-2" /><a href={`tel:${selected.phone}`}>{selected.phone}</a></div>
                )}

                {/* Round ORG-2 (Issues 3/4) — panel actions */}
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <button
                    className="btn btn-light btn-sm border"
                    onClick={() => chartRef.current?.setUpToTheRootHighlighted(selected.id).setCentered(selected.id).render()}
                  >
                    <i className="ri-focus-3-line me-1" />Center
                  </button>
                  {/* Go-live parity (2026-09-24): HR shortcut to the employee editor
                      (title, photo, supervisor — legacy edited these in the chart). */}
                  {canEditInHr && (
                    <Link
                      className="btn btn-light btn-sm border"
                      to="/humanresource?section=employees&view=list"
                      title="Edit title, photo or supervisor in HR › Employees"
                    >
                      <i className="ri-edit-2-line me-1" />Edit in HR
                    </Link>
                  )}
                  {(selected.directReports || 0) > 0 && focusRootId !== selected.id && (
                    <button
                      className="btn btn-light btn-sm border"
                      onClick={() => { setFocusRootId(selected.id); setSelected(null); }}
                      title="Re-root the visible chart on this person and their team"
                    >
                      <i className="ri-crosshair-2-line me-1" />Focus chart here
                    </button>
                  )}
                  {panelData?.subs?.length > 0 && (
                    <button
                      className="btn btn-light btn-sm border"
                      onClick={() => exportCsv([byId[selected.id], ...panelData.subs].filter(Boolean), `team-${selected.name.replace(/\s+/g, "-")}.csv`)}
                      title="Download this person + their whole team as a CSV"
                    >
                      <i className="ri-download-2-line me-1" />Export team
                    </button>
                  )}
                </div>

                {panelData && (
                  <>
                    <h6 className="mt-3">Team ({panelData.subs.length} total)</h6>
                    {Object.keys(panelData.breakdown).length > 0 && (
                      <div className="mb-2">
                        {Object.entries(panelData.breakdown).map(([d, c]) => (
                          <Badge key={d} color="light" className="border me-1 mb-1">{d}: {c}</Badge>
                        ))}
                      </div>
                    )}
                    <div className="ocp-sub-list">
                      {panelData.subs.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className="ocp-sub-row"
                          onClick={() => {
                            chartRef.current?.setUpToTheRootHighlighted(s.id).setCentered(s.id).render();
                            setSelected(byId[s.id]);
                          }}
                          title="Center on this person"
                        >
                          <img src={s.photo || MyImg} alt="" />
                          <span className="ocp-sub-name">{s.name}</span>
                          <span className="ocp-sub-title">{s.title}</span>
                        </button>
                      ))}
                      {panelData.subs.length === 0 && (
                        <div className="text-muted small">No direct reports.</div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </OffcanvasBody>
        </Offcanvas>
      </CardBody>
    </Card>
  );
};

export default OrgChartPro;
